/**
 * MailChimp Campaign Details Component
 * Modal for displaying comprehensive campaign information in table format
 */

import React, { useState, useEffect } from 'react';
import { useNotifications } from '../common/NotificationSystem';
import { mailchimpService } from '../../services/mailchimpService';
import ProfessionalLoader from "../common/ProfessionalLoader";
import './MailChimpCampaignDetails.css';

const MailChimpCampaignDetails = ({ 
  campaign, 
  onClose, 
  onSend, 
  canManage, 
  isProcessing 
}) => {
  const { showError } = useNotifications();
  const [detailedReports, setDetailedReports] = useState(null);
  const [isLoadingReports, setIsLoadingReports] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');

  // Load detailed campaign reports
  useEffect(() => {
    const loadDetailedReports = async () => {
      if (campaign.id) {
        setIsLoadingReports(true);
        try {
          const response = await mailchimpService.getCampaignReports(campaign.id);
          if (response.success) {
            setDetailedReports(response.data);
          }
        } catch (error) {
          showError('Failed to load detailed campaign reports', 'Error');
        } finally {
          setIsLoadingReports(false);
        }
      }
    };

    loadDetailedReports();
  }, [campaign.id, showError]);

  if (!campaign) return null;

  const canSend = canManage && campaign.status === 'save';

  // Helper function to format values
  const formatValue = (value, type = 'text') => {
    if (value === null || value === undefined || value === '') {
      return 'N/A';
    }
    
    switch (type) {
      case 'date':
        return new Date(value).toLocaleString();
      case 'percentage':
        return typeof value === 'number' ? `${(value * 100).toFixed(2)}%` : value;
      case 'boolean':
        return value ? 'Yes' : 'No';
      case 'currency':
        return typeof value === 'number' ? `$${value.toFixed(2)}` : value;
      case 'number':
        return typeof value === 'number' ? value.toLocaleString() : value;
      default:
        return String(value);
    }
  };


  // Create comprehensive data structure for table
  const createCampaignDataTable = () => {
    const data = [];
    
    // Basic Campaign Information
    data.push({ category: 'Basic Information', fields: [
      { label: 'Campaign ID', value: campaign.id, type: 'text' },
      { label: 'Campaign Title', value: campaign.settings?.title || detailedReports?.campaign_title, type: 'text' },
      { label: 'Subject Line', value: campaign.settings?.subject_line || detailedReports?.subject_line, type: 'text' },
      { label: 'Preview Text', value: detailedReports?.preview_text, type: 'text' },
      { label: 'Type', value: campaign.type, type: 'text' },
      { label: 'Status', value: campaign.status, type: 'text' },
      { label: 'Content Type', value: campaign.content_type, type: 'text' },
      { label: 'Create Time', value: campaign.create_time, type: 'date' },
      { label: 'Send Time', value: campaign.send_time || detailedReports?.send_time, type: 'date' },
      { label: 'Archive URL', value: campaign.archive_url, type: 'text' }
    ]});

    // List Information
    if (detailedReports) {
      data.push({ category: 'List Information', fields: [
        { label: 'List ID', value: detailedReports.list_id, type: 'text' },
        { label: 'List Name', value: detailedReports.list_name, type: 'text' },
        { label: 'List Active', value: detailedReports.list_is_active, type: 'boolean' }
      ]});
    }

    // Campaign Settings
    if (campaign.settings) {
      data.push({ category: 'Campaign Settings', fields: [
        { label: 'From Name', value: campaign.settings.from_name, type: 'text' },
        { label: 'Reply To', value: campaign.settings.reply_to, type: 'text' },
        { label: 'To Name', value: campaign.settings.to_name, type: 'text' },
        { label: 'Folder ID', value: campaign.settings.folder_id, type: 'text' },
        { label: 'Template ID', value: campaign.settings.template_id, type: 'number' },
        { label: 'Use Conversation', value: campaign.settings.use_conversation, type: 'boolean' },
        { label: 'Authenticate', value: campaign.settings.authenticate, type: 'boolean' },
        { label: 'Auto Footer', value: campaign.settings.auto_footer, type: 'boolean' },
        { label: 'Inline CSS', value: campaign.settings.inline_css, type: 'boolean' },
        { label: 'Auto Tweet', value: campaign.settings.auto_tweet, type: 'boolean' },
        { label: 'Facebook Comments', value: campaign.settings.fb_comments, type: 'boolean' },
        { label: 'Timewarp', value: campaign.settings.timewarp, type: 'boolean' },
        { label: 'Drag and Drop', value: campaign.settings.drag_and_drop, type: 'boolean' }
      ]});
    }

    // Email Statistics
    data.push({ category: 'Email Statistics', fields: [
      { label: 'Emails Sent', value: campaign.emails_sent || detailedReports?.emails_sent, type: 'number' },
      { label: 'Abuse Reports', value: detailedReports?.abuse_reports, type: 'number' },
      { label: 'Unsubscribed', value: detailedReports?.unsubscribed, type: 'number' }
    ]});

    // Tracking Settings
    if (campaign.tracking) {
      data.push({ category: 'Tracking Settings', fields: [
        { label: 'Opens Tracking', value: campaign.tracking.opens, type: 'boolean' },
        { label: 'HTML Clicks Tracking', value: campaign.tracking.html_clicks, type: 'boolean' },
        { label: 'Text Clicks Tracking', value: campaign.tracking.text_clicks, type: 'boolean' },
        { label: 'Goal Tracking', value: campaign.tracking.goal_tracking, type: 'boolean' },
        { label: 'Ecommerce 360', value: campaign.tracking.ecomm360, type: 'boolean' },
        { label: 'Google Analytics', value: campaign.tracking.google_analytics, type: 'text' },
        { label: 'Clicktale', value: campaign.tracking.clicktale, type: 'text' }
      ]});
    }

    // Report Summary (from campaign list response)
    if (campaign.report_summary) {
      data.push({ category: 'Report Summary (List)', fields: [
        { label: 'Total Opens', value: campaign.report_summary.opens, type: 'number' },
        { label: 'Unique Opens', value: campaign.report_summary.unique_opens, type: 'number' },
        { label: 'Open Rate', value: campaign.report_summary.open_rate, type: 'percentage' },
        { label: 'Total Clicks', value: campaign.report_summary.clicks, type: 'number' },
        { label: 'Subscriber Clicks', value: campaign.report_summary.subscriber_clicks, type: 'number' },
        { label: 'Click Rate', value: campaign.report_summary.click_rate, type: 'percentage' }
      ]});
    }

    // Detailed Opens (from detailed response)
    if (detailedReports?.opens) {
      data.push({ category: 'Detailed Opens', fields: [
        { label: 'Opens Total', value: detailedReports.opens.opens_total, type: 'number' },
        { label: 'Proxy Excluded Opens', value: detailedReports.opens.proxy_excluded_opens, type: 'number' },
        { label: 'Unique Opens', value: detailedReports.opens.unique_opens, type: 'number' },
        { label: 'Proxy Excluded Unique Opens', value: detailedReports.opens.proxy_excluded_unique_opens, type: 'number' },
        { label: 'Open Rate', value: detailedReports.opens.open_rate, type: 'percentage' },
        { label: 'Proxy Excluded Open Rate', value: detailedReports.opens.proxy_excluded_open_rate, type: 'percentage' },
        { label: 'Last Open', value: detailedReports.opens.last_open, type: 'date' }
      ]});
    }

    // Detailed Clicks (from detailed response)
    if (detailedReports?.clicks) {
      data.push({ category: 'Detailed Clicks', fields: [
        { label: 'Clicks Total', value: detailedReports.clicks.clicks_total, type: 'number' },
        { label: 'Unique Clicks', value: detailedReports.clicks.unique_clicks, type: 'number' },
        { label: 'Unique Subscriber Clicks', value: detailedReports.clicks.unique_subscriber_clicks, type: 'number' },
        { label: 'Click Rate', value: detailedReports.clicks.click_rate, type: 'percentage' },
        { label: 'Last Click', value: detailedReports.clicks.last_click, type: 'date' }
      ]});
    }

    // Bounces
    if (detailedReports?.bounces) {
      data.push({ category: 'Bounces', fields: [
        { label: 'Hard Bounces', value: detailedReports.bounces.hard_bounces, type: 'number' },
        { label: 'Soft Bounces', value: detailedReports.bounces.soft_bounces, type: 'number' },
        { label: 'Syntax Errors', value: detailedReports.bounces.syntax_errors, type: 'number' }
      ]});
    }

    // Forwards
    if (detailedReports?.forwards) {
      data.push({ category: 'Forwards', fields: [
        { label: 'Forwards Count', value: detailedReports.forwards.forwards_count, type: 'number' },
        { label: 'Forwards Opens', value: detailedReports.forwards.forwards_opens, type: 'number' }
      ]});
    }

    // Facebook Likes
    if (detailedReports?.facebook_likes) {
      data.push({ category: 'Facebook Likes', fields: [
        { label: 'Recipient Likes', value: detailedReports.facebook_likes.recipient_likes, type: 'number' },
        { label: 'Unique Likes', value: detailedReports.facebook_likes.unique_likes, type: 'number' },
        { label: 'Facebook Likes', value: detailedReports.facebook_likes.facebook_likes, type: 'number' }
      ]});
    }

    // Industry Stats
    if (detailedReports?.industry_stats) {
      data.push({ category: 'Industry Statistics', fields: [
        { label: 'Industry Type', value: detailedReports.industry_stats.type, type: 'text' },
        { label: 'Industry Open Rate', value: detailedReports.industry_stats.open_rate, type: 'percentage' },
        { label: 'Industry Click Rate', value: detailedReports.industry_stats.click_rate, type: 'percentage' },
        { label: 'Industry Bounce Rate', value: detailedReports.industry_stats.bounce_rate, type: 'percentage' },
        { label: 'Industry Unopen Rate', value: detailedReports.industry_stats.unopen_rate, type: 'percentage' },
        { label: 'Industry Unsub Rate', value: detailedReports.industry_stats.unsub_rate, type: 'percentage' },
        { label: 'Industry Abuse Rate', value: detailedReports.industry_stats.abuse_rate, type: 'percentage' }
      ]});
    }

    // List Stats
    if (detailedReports?.list_stats) {
      data.push({ category: 'List Statistics', fields: [
        { label: 'Sub Rate', value: detailedReports.list_stats.sub_rate, type: 'number' },
        { label: 'Unsub Rate', value: detailedReports.list_stats.unsub_rate, type: 'number' },
        { label: 'List Open Rate', value: detailedReports.list_stats.open_rate, type: 'number' },
        { label: 'List Proxy Excluded Open Rate', value: detailedReports.list_stats.proxy_excluded_open_rate, type: 'number' },
        { label: 'List Click Rate', value: detailedReports.list_stats.click_rate, type: 'number' }
      ]});
    }

    // Ecommerce
    if (detailedReports?.ecommerce || campaign.report_summary?.ecommerce) {
      const ecommerce = detailedReports?.ecommerce || campaign.report_summary?.ecommerce;
      data.push({ category: 'Ecommerce', fields: [
        { label: 'Total Orders', value: ecommerce.total_orders, type: 'number' },
        { label: 'Total Spent', value: ecommerce.total_spent, type: 'currency' },
        { label: 'Total Revenue', value: ecommerce.total_revenue, type: 'currency' },
        { label: 'Currency Code', value: ecommerce.currency_code, type: 'text' }
      ]});
    }

    // Delivery Status
    if (detailedReports?.delivery_status) {
      data.push({ category: 'Delivery Status', fields: [
        { label: 'Delivery Status Enabled', value: detailedReports.delivery_status.enabled, type: 'boolean' }
      ]});
    }

    // Timeseries (summary)
    if (detailedReports?.timeseries && Array.isArray(detailedReports.timeseries)) {
      data.push({ category: 'Timeseries Summary', fields: [
        { label: 'Total Data Points', value: detailedReports.timeseries.length, type: 'number' },
        { label: 'First Timestamp', value: detailedReports.timeseries[0]?.timestamp, type: 'date' },
        { label: 'Last Timestamp', value: detailedReports.timeseries[detailedReports.timeseries.length - 1]?.timestamp, type: 'date' }
      ]});
    }

    return data;
  };

  const campaignData = createCampaignDataTable();

  // Filter data based on search term
  const filteredCampaignData = campaignData.map(category => ({
    ...category,
    fields: category.fields.filter(field => 
      field.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      formatValue(field.value, field.type).toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.fields.length > 0);

  // Get key metrics for overview
  const getKeyMetrics = () => {
    const metrics = {
      emailsSent: campaign.emails_sent || detailedReports?.emails_sent || 0,
      openRate: campaign.report_summary?.open_rate || detailedReports?.opens?.open_rate || 0,
      clickRate: campaign.report_summary?.click_rate || detailedReports?.clicks?.click_rate || 0,
      uniqueOpens: campaign.report_summary?.unique_opens || detailedReports?.opens?.unique_opens || 0,
      totalClicks: campaign.report_summary?.clicks || detailedReports?.clicks?.clicks_total || 0,
      status: campaign.status,
      type: campaign.type,
      sendTime: campaign.send_time || detailedReports?.send_time
    };
    return metrics;
  };

  const keyMetrics = getKeyMetrics();

  return (
    <div className="campaign-details-overlay">
      <div className="campaign-details-modal">
        {/* Enhanced Header */}
        <div className="modal-header">
          <div className="header-content">
            <div className="campaign-title-section">
              <h2>📧 {campaign.settings?.title || 'Campaign Details'}</h2>
              <div className="campaign-meta">
                <span className="campaign-id">ID: {campaign.id}</span>
                <span className={`status-badge ${campaign.status}`}>
                  {campaign.status}
                </span>
                <span className="campaign-type">{campaign.type}</span>
              </div>
            </div>
          </div>
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        </div>

        {/* Navigation Tabs */}
        <div className="modal-navigation">
          <div className="tab-buttons">
            <button 
              className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              📊 Overview
            </button>
            <button 
              className={`tab-button ${activeTab === 'details' ? 'active' : ''}`}
              onClick={() => setActiveTab('details')}
            >
              📋 Details
            </button>
            <button 
              className={`tab-button ${activeTab === 'analytics' ? 'active' : ''}`}
              onClick={() => setActiveTab('analytics')}
            >
              📈 Analytics
            </button>
            {detailedReports?.timeseries && (
              <button 
                className={`tab-button ${activeTab === 'timeseries' ? 'active' : ''}`}
                onClick={() => setActiveTab('timeseries')}
              >
                ⏰ Timeline
              </button>
            )}
          </div>
        </div>
        
        <div className="modal-content">
          {/* Loading State */}
          {isLoadingReports && (
            <div className="loading-section">
              <ProfessionalLoader size="large" variant="default" />
              <p>Loading detailed campaign reports...</p>
            </div>
          )}

          {/* Overview Tab */}
          {!isLoadingReports && activeTab === 'overview' && (
            <div className="overview-tab">
              {/* Key Metrics Cards */}
              <div className="metrics-grid">
                <div className="metric-card primary">
                  <div className="metric-icon">📧</div>
                  <div className="metric-content">
                    <h3>Emails Sent</h3>
                    <p className="metric-value">{keyMetrics.emailsSent.toLocaleString()}</p>
              </div>
              </div>
                <div className="metric-card success">
                  <div className="metric-icon">👁️</div>
                  <div className="metric-content">
                    <h3>Open Rate</h3>
                    <p className="metric-value">{(keyMetrics.openRate * 100).toFixed(1)}%</p>
                    <p className="metric-detail">{keyMetrics.uniqueOpens.toLocaleString()} opens</p>
              </div>
              </div>
                <div className="metric-card info">
                  <div className="metric-icon">🖱️</div>
                  <div className="metric-content">
                    <h3>Click Rate</h3>
                    <p className="metric-value">{(keyMetrics.clickRate * 100).toFixed(1)}%</p>
                    <p className="metric-detail">{keyMetrics.totalClicks.toLocaleString()} clicks</p>
              </div>
              </div>
                <div className="metric-card warning">
                  <div className="metric-icon">📅</div>
                  <div className="metric-content">
                    <h3>Send Time</h3>
                    <p className="metric-value">
                      {keyMetrics.sendTime ? 
                        new Date(keyMetrics.sendTime).toLocaleDateString() : 
                    'Not sent'
                  }
                    </p>
              </div>
            </div>
          </div>

              {/* Campaign Summary */}
              <div className="campaign-summary">
                <h3>📋 Campaign Summary</h3>
                <div className="summary-grid">
                  <div className="summary-item">
                    <label>Subject Line</label>
                    <span>{campaign.settings?.subject_line || 'N/A'}</span>
                    </div>
                  <div className="summary-item">
                    <label>From Name</label>
                    <span>{campaign.settings?.from_name || 'N/A'}</span>
                  </div>
                  <div className="summary-item">
                    <label>Reply To</label>
                    <span>{campaign.settings?.reply_to || 'N/A'}</span>
                    </div>
                  <div className="summary-item">
                    <label>Content Type</label>
                    <span>{campaign.content_type || 'N/A'}</span>
                  </div>
                  <div className="summary-item">
                    <label>Create Time</label>
                    <span>{formatValue(campaign.create_time, 'date')}</span>
                  </div>
                  <div className="summary-item">
                    <label>Archive URL</label>
                    <span>
                      {campaign.archive_url ? (
                        <a href={campaign.archive_url} target="_blank" rel="noopener noreferrer">
                          View Archive
                        </a>
                      ) : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Details Tab */}
          {!isLoadingReports && activeTab === 'details' && (
            <div className="details-tab">
              <div className="search-section">
                <div className="search-box">
                  <input
                    type="text"
                    placeholder="Search fields..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                  <span className="search-icon">🔍</span>
                </div>
            </div>

              <div className="details-content">
                {filteredCampaignData.map((category, categoryIndex) => (
                  <div key={categoryIndex} className="category-section">
                    <h3 className="category-title">{category.category}</h3>
                    <div className="fields-grid">
                      {category.fields.map((field, fieldIndex) => (
                        <div key={fieldIndex} className="field-item">
                          <label className="field-label">{field.label}</label>
                          <span className={`field-value ${field.type}`}>
                            {formatValue(field.value, field.type)}
                          </span>
                    </div>
                      ))}
                    </div>
                  </div>
                ))}
                  </div>
                </div>
              )}

          {/* Analytics Tab */}
          {!isLoadingReports && activeTab === 'analytics' && (
            <div className="analytics-tab">
              <div className="analytics-grid">
                {/* Performance Metrics */}
                <div className="analytics-section">
                  <h3>📊 Performance Metrics</h3>
                  <div className="metrics-cards">
                    {campaign.report_summary && (
                      <>
                        <div className="metric-card">
                          <h4>Opens</h4>
                          <p className="metric-number">{campaign.report_summary.opens || 0}</p>
                          <p className="metric-rate">{(campaign.report_summary.open_rate * 100).toFixed(1)}% rate</p>
                    </div>
                        <div className="metric-card">
                          <h4>Clicks</h4>
                          <p className="metric-number">{campaign.report_summary.clicks || 0}</p>
                          <p className="metric-rate">{(campaign.report_summary.click_rate * 100).toFixed(1)}% rate</p>
                    </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Industry Comparison */}
                {detailedReports?.industry_stats && (
                  <div className="analytics-section">
                    <h3>🏭 Industry Comparison</h3>
                    <div className="comparison-grid">
                      <div className="comparison-item">
                        <label>Your Open Rate</label>
                        <span className="your-rate">{(keyMetrics.openRate * 100).toFixed(1)}%</span>
                    </div>
                      <div className="comparison-item">
                        <label>Industry Average</label>
                        <span className="industry-rate">{(detailedReports.industry_stats.open_rate * 100).toFixed(1)}%</span>
                    </div>
                    </div>
                  </div>
                )}
                </div>
                    </div>
                  )}

          {/* Timeseries Tab */}
          {!isLoadingReports && activeTab === 'timeseries' && detailedReports?.timeseries && (
            <div className="timeseries-tab">
              <div className="timeseries-header">
                <h3>📈 Campaign Timeline</h3>
                <p>Performance over time</p>
                </div>
              
              <div className="timeseries-table-container">
                <table className="timeseries-table">
                  <thead>
                    <tr>
                      <th>Timestamp</th>
                      <th>Emails Sent</th>
                      <th>Unique Opens</th>
                      <th>Recipients Clicks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailedReports.timeseries.map((dataPoint, index) => (
                      <tr key={index}>
                        <td>{formatValue(dataPoint.timestamp, 'date')}</td>
                        <td>{formatValue(dataPoint.emails_sent, 'number')}</td>
                        <td>{formatValue(dataPoint.unique_opens, 'number')}</td>
                        <td>{formatValue(dataPoint.recipients_clicks, 'number')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        
        <div className="modal-actions">
          <button className="btn btn-outline" onClick={onClose}>
            Close
          </button>
          {canSend && (
            <button
              className="btn btn-primary"
              onClick={() => {
                onSend(campaign.id);
                onClose();
              }}
              disabled={isProcessing}
            >
              {isProcessing ? 'Sending...' : 'Send Campaign'}
            </button>
          )}
          {campaign.archive_url && (
            <a
              href={campaign.archive_url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline"
            >
              View Archive
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default MailChimpCampaignDetails;
